import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { PeriodSelector } from '../components/ui/PeriodSelector';
import { getProjection, ProjectionItem } from '../services/apiService';
import { formatBRL } from '../utils/FormatDecimal';
import { Check, Clock, AlertCircle } from 'lucide-react';

export default function ProjectionPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getProjection({ year, month });
      setData(result);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao carregar projeção');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) return <LoadingSpinner message="Carregando projeção..." />;
  if (error) return <EmptyState title="Erro" description={error} />;
  if (!data) return <EmptyState title="Sem dados" description="Nenhum dado disponível" />;

  const { projections, summary } = data;

  const saidas = projections.filter((p: ProjectionItem) => p.direction === 'saida');
  const entradas = projections.filter((p: ProjectionItem) => p.direction === 'entrada');

  const renderTable = (items: ProjectionItem[], title: string, colorClass: string) => {
    if (items.length === 0) return null;

    const totalProjected = items.reduce((s, p) => s + Math.abs(p.realized ? (p.realized_amount || 0) : p.expected_amount), 0);
    const totalRealized = items.reduce((s, p) => s + (p.realized ? Math.abs(p.realized_amount || 0) : 0), 0);
    const totalPending = items.filter(p => !p.realized).reduce((s, p) => s + Math.abs(p.expected_amount), 0);

    return (
      <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              {title}
            </h3>
            <div className="flex gap-4 text-xs text-gray-500">
              <span>Projetado: <strong className={colorClass}>{formatBRL(totalProjected)}</strong></span>
              <span>Realizado: <strong className="text-gray-700">{formatBRL(totalRealized)}</strong></span>
              <span>Pendente: <strong className="text-amber-600">{formatBRL(totalPending)}</strong></span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-center py-2 px-2 font-medium text-gray-500 w-10">Status</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Dia</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Conta</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Descrição</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500">Esperado</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500">Realizado</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p, i) => {
                  const stripe = i % 2 === 1;
                  // Simplify account name: remove first segment
                  const shortAccount = p.account.split(':').slice(1).join(':') || p.account;
                  return (
                    <tr key={p.account} className={`border-b border-gray-100 hover:bg-blue-50/50 ${stripe ? 'bg-gray-50' : ''}`}>
                      <td className="py-2 px-2 text-center">
                        {p.realized ? (
                          <Check className="h-4 w-4 text-green-500 mx-auto" />
                        ) : (
                          <Clock className="h-4 w-4 text-amber-400 mx-auto" />
                        )}
                      </td>
                      <td className="py-2 px-3 text-gray-500 font-mono text-xs">{String(p.typical_day).padStart(2, '0')}</td>
                      <td className="py-2 px-3 text-gray-700 text-xs">{shortAccount}</td>
                      <td className="py-2 px-3 text-gray-600">{p.payee}</td>
                      <td className="py-2 px-3 text-right font-mono text-gray-500">{formatBRL(Math.abs(p.expected_amount))}</td>
                      <td className={`py-2 px-3 text-right font-mono font-medium ${p.realized ? 'text-gray-800' : 'text-gray-300'}`}>
                        {p.realized ? formatBRL(Math.abs(p.realized_amount || 0)) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    );
  };

  const pendingCount = projections.filter((p: ProjectionItem) => !p.realized).length;
  const realizedCount = projections.filter((p: ProjectionItem) => p.realized).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodSelector year={year} month={month} onChange={(y, m) => { setYear(y); setMonth(m); }} />
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span className="flex items-center gap-1"><Check className="h-4 w-4 text-green-500" /> {realizedCount} realizados</span>
          <span className="flex items-center gap-1"><Clock className="h-4 w-4 text-amber-400" /> {pendingCount} pendentes</span>
          {loading && <span className="text-xs text-gray-400">Atualizando...</span>}
        </div>
      </div>

      {/* Summary cards */}
      {(() => {
        // Projeção ajustada: realizado onde já existe + esperado pro que falta
        const projectedIn = projections.reduce((s: number, p: ProjectionItem) => {
          if (p.direction !== 'entrada') return s;
          return s + Math.abs(p.realized ? (p.realized_amount || 0) : p.expected_amount);
        }, 0);
        const projectedOut = projections.reduce((s: number, p: ProjectionItem) => {
          if (p.direction !== 'saida') return s;
          return s + Math.abs(p.realized ? (p.realized_amount || 0) : p.expected_amount);
        }, 0);
        const resultadoProjetado = projectedIn - projectedOut;
        const resultadoRealizado = summary.total_realized_entradas - summary.total_realized_saidas;
        return (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card>
              <CardContent>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Entradas Projetadas</p>
                <p className="text-lg font-semibold text-green-600 font-mono mt-1">{formatBRL(projectedIn)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Saídas Projetadas</p>
                <p className="text-lg font-semibold text-red-600 font-mono mt-1">{formatBRL(projectedOut)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Resultado Projetado</p>
                <p className={`text-lg font-semibold font-mono mt-1 ${resultadoProjetado >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>{formatBRL(resultadoProjetado)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Entradas Realizadas</p>
                <p className="text-lg font-semibold text-green-700 font-mono mt-1">{formatBRL(summary.total_realized_entradas)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Saídas Realizadas</p>
                <p className="text-lg font-semibold text-red-700 font-mono mt-1">{formatBRL(summary.total_realized_saidas)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Resultado Realizado</p>
                <p className={`text-lg font-semibold font-mono mt-1 ${resultadoRealizado >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>{formatBRL(resultadoRealizado)}</p>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {renderTable(saidas, 'Saídas Previstas', 'text-red-600')}
      {renderTable(entradas, 'Entradas Previstas', 'text-green-600')}
    </div>
  );
}
